import { useMemo } from "react";
import { usePath } from "@/contexts/path/path";
import SEO from "../seo";
import { financialFormat } from "@/lib/helpers";

const imagesBaseURL = "https://images.aqar.fm/webp/1080x0/props/"; // adjust if needed

const SCHEMA_MAPPING = {
  "1": "Apartment",
  "2": "Land",
  "3": "Villa",
  "4": "Apartment",
  "5": "Villa",
  "6": "Apartment",
  "7": "Building",
  "8": "Store",
  "9": "House",
  "10": "House",
  "11": "House",
  "12": "House",
  "13": "Accommodation",
  "14": "Office",
  "15": "Land",
  "16": "Building",
  "17": "Warehouse",
  "20": "Store",
  "22": "Apartment",
  "23": "Accommodation",
  "24": "Place",
  "101": "Accommodation",
  "102": "Accommodation",
  "103": "Accommodation",
  "104": "Accommodation",
  "106": "Accommodation",
  "107": "Accommodation",
  "108": "Accommodation"
}

const SPECIAL_FILTERS = {
  rent_period: {
    1: "يومي",
    2: "عقد شهري",
    3: "عقد سنوي",
  },
  price_12_payments: "دفع شهري 12 دفعة",
  price_2_payments: "دفع كل ستة شهور دفعتين",
  price_4_payments: "دفع كل 3 شهور أربعة دفعات",
};
const defaultKeywords = "تطبيق,عقار,العقار,السعودي,عقارات السعودية";
function getListingKeywords(listing, searchFields) {
  const keywords = searchFields?.map((field) => {
    if (SPECIAL_FILTERS[field.name]) {
      if (typeof SPECIAL_FILTERS[field.name] == "string")
        return SPECIAL_FILTERS[field.name];
      return SPECIAL_FILTERS[field.name][listing[field.name]];
    }
    if (typeof field?.options?.map === "function") {
      return [
        field.label?.ar,
        field.options.find((option) => option.value === listing[field.name])
          ?.label?.ar,
      ]
        .filter(Boolean)
        .join(" ");
    } else {
      return [field.label?.ar, listing[field.name]?.label?.ar]
        .filter(Boolean)
        .join(" ");
    }
  });
  return keywords?.filter(Boolean).join(",");
}
export function getTitle({
  filters = {},
  district,
  searchFields,
  city,
  direction,
  quickFields,
  page,
  category,
  listing,
  basePath,
  filtersAsText,
}) {
  if (listing)
    return [
      listing?.title ? listing.title + " - " + listing.id : null,
      getListingKeywords(listing, searchFields),
    ];
  const searchFieldsMap = searchFields?.reduce(
    (acc, item) => ({ ...acc, [item.name]: item }),
    {},
  );
  const quickFieldsMap =
    quickFields
      ?.flat(1)
      ?.reduce((acc, item) => ({ ...acc, [item.name]: item }), {}) || {};
  const title = "";
  const keys = Object.keys(filters);

  if (!keys.length) {
    return [title, ""];
  }
  const filterString = [];
  if (!quickFieldsMap || !searchFieldsMap) return "";
  if (filtersAsText) return [filtersAsText, ""];

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const field = quickFieldsMap[key] || searchFieldsMap[key];
    if (SPECIAL_FILTERS[key] && filters[key]) {
      if (typeof SPECIAL_FILTERS[key] == "string")
        filterString.push(SPECIAL_FILTERS[key]);
      else filterString.push(SPECIAL_FILTERS[key]?.[filters[key]?.["eq"]]);
      continue;
    }
    if ((quickFieldsMap[key] || searchFieldsMap[key])?.options) {
      const { label } =
        (quickFieldsMap[key] || searchFieldsMap[key])?.options?.find((_) => {
          if (_.matcher && filters[key]) {
            const matcher = Object.keys(filters[key])
              .map((k) => `${k},${filters[key][k]}`)
              .join(",");
            return _.matcher === matcher;
          }
          return (
            _.value ===
            +filters[key]?.[
              (quickFieldsMap[key] || searchFieldsMap[key])?.operation
            ]
          );
        }) || {};

      if (/^[0-9]\+?$/.test(label?.ar)) {
        filterString.push(
          (quickFieldsMap[key] || searchFieldsMap[key]).label?.ar +
            " " +
            label?.ar +
            (label?.ar.includes("+") ? " فأكثر" : ""),
        );
      } else
        filterString.push(
          key === "street_width"
            ? `${field?.label?.ar || ""} ${label?.ar || ""}`
            : label?.alt?.ar || label?.ar,
        );
    } else {
      if ("price" === key && filters[key]) {
        if (filters[key].gte == 1 && filters["rent_period"]?.eq == 3) {
          filterString.push("دفعة واحدة");
          continue;
        }

        filterString.push(
          "السعر " +
            [
              financialFormat(filters[key].gte),
              financialFormat(filters[key].lte),
            ]
              .filter((a) => a)
              .join(" - "),
          "ريال",
        );
        continue;
      } else if ("area" === key && filters[key]) {
        filterString.push(
          "المساحة " +
            [filters[key].gte, filters[key].lte].filter((a) => a).join(" - "),
        );
        continue;
      }

      const field = quickFieldsMap[key] || searchFieldsMap[key];
      if ("boolean,checkbox".includes(field?.type)) {
        if (filters[key]?.[field?.operation])
          filterString.push(`${field?.label?.ar || ""} `);
        continue;
      }
      const value = filters[key]?.[field?.operation];
      if (value) filterString.push(`${field?.label?.ar || ""} ${value || ""}`);
    }
  }

  return [
    filterString
      .filter((s) => s?.trim())
      .join(" ")
      .trim(),
    filterString.join(","),
  ];
}





const AMENITY_MAP = {
  ac: "ac",
  wifi: "wifi",
  washing_machine: "washerDryer",
  smart_tv: "tv",
  self_entry: "selfCheckinCheckout",
  oven: "ovenStove",
  heater: "heating",
  iron: "ironingBoard",
  smoking: "smokingAllowed",
  special_view: null,
  cleaning_service: null,
  microwave: "microwave",
  private_beach: "privateBeachAccess",
  instant_booking: "instantBookable",
  balcony: "balcony",
  pool: "pool",
  elevator: "elevator",
  lift: "elevator",
  child_friendly: "childFriendly",
  crib: "crib",
  gym: "gymFitnessEquipment",
  fireplace: "fireplace",
  outdoor_grill: "outdoorGrill",
  patio: "patio",
  pets_allowed: "petsAllowed",
  airport_shuttle: "airportShuttle"
};

function mapAmenitiesToGoogle(listing) {
  const details = listing.extended_details || {};
  const amenities = [];

  for (const key in AMENITY_MAP) {
    const googleKey = AMENITY_MAP[key];
    if (!googleKey) continue;

    // Check both listing.extended_details and listing directly
    const value = details[key] ?? listing[key];
    if (value) {
      amenities.push({
        "@type": "LocationFeatureSpecification",
        "name": googleKey,
        "value": true
      });
    }
  }

  return amenities;
}


function generateVacationRentalJSONLD(listing,path) {
  // Map extended_details to amenities

  const amenities = mapAmenitiesToGoogle(listing);

  // Build beds array dynamically
  const beds = [];
  const totalBeds = listing.beds || 1;
  for (let i = 0; i < totalBeds; i++) {
    beds.push({
      "@type": "BedDetails",
      "numberOfBeds": 1,
      "typeOfBed": i === 0 ? "King" : "Single"
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    name: listing.title || "Vacation Rental",
    identifier: String(listing.id),
    containsPlace: {
      "@type": "Accommodation",
      numberOfRooms:  listing.beds || 1,
      numberOfBedrooms: listing.extended_details?.bedrooms_count || 0,
      numberOfBathroomsTotal: listing.wc || 1,
      floorSize: {
        "@type": "QuantitativeValue",
        value: listing.area || 0,
        unitCode: "SQM",
      },
      occupancy: {
        "@type": "QuantitativeValue",
        value: listing.extended_details?.capacity || 2,
      },
      bed: beds,
      amenityFeature: amenities,
    },
    image: (listing.imgs || []).map((img) => imagesBaseURL + img),
    latitude: listing.location?.lat,
    longitude: listing.location?.lng,
    address: {
      "@type": "PostalAddress",
      streetAddress: listing.address || "",
      addressLocality: path?.district?.name || "",
      addressRegion: path?.city?.name || "",
      addressCountry: "SA",
    },
    description: listing.content || "",
    checkinTime: listing.extended_details?.check_in_hour
      ? `${String(listing.extended_details.check_in_hour).padStart(2, "0")}:00:00+03:00`
      : undefined,
    checkoutTime: listing.extended_details?.check_out_hour
      ? `${String(listing.extended_details.check_out_hour).padStart(2, "0")}:00:00+03:00`
      : undefined,
  };
}


function generateRealEstateLD(listing,path) {

  // Map images
  const images = listing.imgs?.length ? listing.imgs.map(img => imagesBaseURL + img) : [];

  const amenityFeature = [];
  if (listing.lift) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "مصعد" });
  if (listing.ketchen) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "مطبخ" });
  if (listing.pool) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "مسبح" });
  if (listing.backyard) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "حديقة خلفية" });
  if (listing.maid) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "غرفة خادمة" });
  if (listing.stairs) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "سلم" });
  if (listing.rooms) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "عدد الغرف", "value": listing.rooms });
  if (listing.wc) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "عدد دورات المياه", "value": listing.wc });
  if (listing.area) amenityFeature.push({ "@type": "LocationFeatureSpecification", "name": "المساحة (متر مربع)", "value": listing.area });

  // Floor size
  const floorSize = {
    "@type": "QuantitativeValue",
    "value": listing.area || 0,
    "unitText": "SQM"
  };

  // Offer
  const offer = {
    "@type": "Offer",
    "price": listing.price || 0,
    "priceCurrency": "SAR",
    "availability": "https://schema.org/InStock",
    "url":process.env.NEXT_PUBLIC_AQAR_WEB_EP + listing.path,
    "validFrom": listing.published_at ? new Date(listing.published_at * 1000).toISOString() : undefined
  };




  return {
    "@context": "https://schema.org",
    name: listing.title || "",
    description: listing.content || "",
    "@type": SCHEMA_MAPPING[listing.category] || "House",
    amenityFeature: amenityFeature,
    floorSize: floorSize,
    image: images.length ? images : undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: listing.address || "",
      addressLocality: path?.district?.name || "",
      addressRegion: path?.city?.name|| "",
      addressCountry: "SA",
    },
    offers: offer,
  };
}


function generateRichResult(listing,path) {

  if(listing.category<100) return generateRealEstateLD(listing,path);


  return  generateVacationRentalJSONLD(listing,path)

}

export default function Title() {
  const path = usePath();
  const { category, city, page, direction, district, listing, basePath } = path;
  const isDaily =
    basePath?.includes("/daily-rent") || basePath?.includes("الحجز-اليومي");

  const categoryPath = isDaily
    ? `/الحجز-اليومي${category?.path || ""}`
    : category?.path;

  const [filterStringTitle, keywords] = useMemo(() => {
    const isDaily =
      basePath?.includes("/daily-rent") || basePath?.includes("الحجز-اليومي");

    const [title, keywords] = getTitle(path);

    const baseKeywords = []
      .concat(path?.listing?.keywords)
      .concat(
        path?.district?.name || "",
        path?.direction?.name || "",
        path?.city?.name || "",
      )
      .concat(path?.category?.keywords)
      .concat(keywords)
      .concat(defaultKeywords)
      .filter(Boolean);

    if (isDaily) baseKeywords.push("ايجار يومي", "شقق مفروشة", "حجز يومي");

    if (path.listing) return [title, baseKeywords.filter(Boolean).join(",")];

    let categoryTitle = category?.plural || "عقارات";
    if (!category?.id && isDaily) categoryTitle = "الحجز اليومي";
    const place = [direction?.name || city?.name, district?.name]
      .filter(Boolean)
      .join(" ");
    return [
      [
        categoryTitle,
        place ? "في " + place : "",
        page > 1 ? ` - الصفحة ${page}` : "",
        title,
      ]
        .filter(Boolean)
        .join(" "),
      baseKeywords.filter(Boolean).join(","),
    ];
  }, [path]);

  const description = useMemo(() => {
    const { listing, district, direction, city, category, basePath } = path;
    if (listing) return listing?.content || listing?.title;

    if (
      basePath?.includes("map/daily-rent") ||
      basePath?.includes("map/الحجز-اليومي")
    )
      return "خريطة عقارية تفاعلية تعرض جميع عقارات السعودية باستخدام خرائط قوقل العربية لتسهيل عملية البحث عن شقق و فلل و بيوت و أراضي وعمائر للحجز اليومي";

    if (basePath?.includes("/daily-rent") || basePath?.includes("الحجز-اليومي"))
      return "شقق و شاليهات و استراحات و مزارع و مخيمات للحجز اليومي على تطبيق عقار بأسعار مميزة و مساحات متنوعة في جميع مدن المملكة بطريقة حجز فورية وسلسة.";

    if (basePath?.includes("map"))
      return "خريطة عقارية تفاعلية تعرض جميع عقارات السعودية باستخدام خرائط قوقل العربية لتسهيل عملية البحث عن شقق و فلل و بيوت و أراضي وعمائر للبيع والإيجار";

    return (
      district?.description ||
      direction?.description ||
      city?.description ||
      category?.description
    );
  }, [path]);
  return (
    <SEO
      description={description}
      keywords={[keywords, path.additionalKeywords].join(",")}
      canonical={`${process.env.NEXT_PUBLIC_AQAR_WEB_EP}${
        listing?.path ||
        district?.path ||
        direction?.path ||
        city?.path ||
        categoryPath ||
        ""
      }${path.filterAsPath || ""}${
        !listing?.path && page > 1 ? `/${page}` : ""
      }`}
      title={filterStringTitle + " | تطبيق عقار"}
      image={
        listing?.imgs?.[0]
          ? `https://images.aqar.fm/webp/350x0/props/${listing?.imgs?.[0]}`
          : null
      }
      preLoads={["https://assets.aqar.fm/icons/search-blue.svg"]}
      structuredData={
        listing ? JSON.stringify(generateRichResult(listing, path)) : undefined
      }
    />
  );
}
